import { useEffect, useCallback, useRef } from 'react';
import { useBreadcrumbs } from '../providers/breadcrumbs/BreadcrumbContext';
import { BreadcrumbProps } from '../stories/Breadcrumb';

/**
 * Hook for injecting breadcrumbs at a specific index in the breadcrumb trail
 * @param id - Unique identifier for this breadcrumb injection (used for cleanup)
 * @param index - The index where breadcrumbs should be injected (0-based)
 * @param breadcrumbs - Array of breadcrumbs to inject
 * @returns cleanup function to remove the injected breadcrumbs
 */
export const useBreadcrumbInjection = (id: string, index: number, breadcrumbs: BreadcrumbProps[]) => {
  const { injectBreadcrumbs, removeInjectedBreadcrumbs } = useBreadcrumbs();
  const lastBreadcrumbsRef = useRef<BreadcrumbProps[]>([]);
  const hasInjectedRef = useRef(false);

  // Inject breadcrumbs when the hook is used
  useEffect(() => {
    // Only inject if breadcrumbs have actually changed OR if we haven't injected yet
    const breadcrumbsChanged =
      breadcrumbs.length !== lastBreadcrumbsRef.current.length ||
      breadcrumbs.some(
        (crumb, i) =>
          crumb.label !== lastBreadcrumbsRef.current[i]?.label || crumb.href !== lastBreadcrumbsRef.current[i]?.href,
      );

    if (breadcrumbsChanged || !hasInjectedRef.current) {
      lastBreadcrumbsRef.current = breadcrumbs;
      if (breadcrumbs.length > 0) {
        injectBreadcrumbs(id, index, breadcrumbs);
        hasInjectedRef.current = true;
      }
      // If breadcrumbs is empty, don't remove existing ones - just don't inject new ones
      // This preserves breadcrumbs during loading states
    }
  }, [id, index, breadcrumbs, injectBreadcrumbs, removeInjectedBreadcrumbs]);

  // Separate effect for cleanup on unmount only
  useEffect(() => {
    return () => {
      removeInjectedBreadcrumbs(id);
      // Reset the injection state so it can be re-injected when component remounts
      hasInjectedRef.current = false;
    };
  }, [id, removeInjectedBreadcrumbs]);

  // Return cleanup function for manual cleanup if needed
  const cleanup = useCallback(() => {
    removeInjectedBreadcrumbs(id);
  }, [id, removeInjectedBreadcrumbs]);

  return cleanup;
};
